import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import Dropdown from '@softwaredev_cegeka/stix-core/dist/components/Dropdown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp'
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe'
import RadioCheckmark from 'src/components/ImpersonationOptionSelector/RadioCheckmark'

// TODO: PORT TO API-CLIENTS
import TranslationsApi from 'src/api/TranslationsApi'
import withTranslations from 'src/HOCs/withTranslations'

import styles from './languageSwitchButton.module.scss'

const SwitcherButton = React.forwardRef((props, ref) => {
    const { selectedLanguage, className, ...rest } = props
    const isOpen = props['aria-expanded']

    delete rest.bsPrefix
    delete rest.startIcon
    delete rest.endIcon

    return (
        <button
            {...rest}
            ref={ref}
            className={cx(styles.switcherButton, className, {
                [styles.isMenuOpen]: isOpen,
            })}
        >
            <FontAwesomeIcon icon={faGlobe} className={styles.globe} />
            <span>{selectedLanguage.language.toUpperCase()}</span>
            <FontAwesomeIcon
                icon={isOpen ? faChevronUp : faChevronDown}
                title={
                    isOpen
                        ? 'Close language switcher'
                        : 'Open language switcher'
                }
                className={styles.chevron}
            />
        </button>
    )
})

SwitcherButton.propTypes = {
    selectedLanguage: PropTypes.shape({
        language: PropTypes.string.isRequired,
    }),
    className: PropTypes.string,
}

class LanguageSwitchButton extends React.Component {
    cancelId = Symbol('LanguageSwitcher')
    translationsClient = new TranslationsApi()
    defaultLanguage = {
        language: 'en',
        label: 'English',
    }

    state = {
        languages: [this.defaultLanguage],
        selectedLanguage: this.defaultLanguage,
        isOpen: false,
    }

    loadLanguages = async () => {
        try {
            const res = await this.translationsClient.fetchLanguages()
            if (res.data?.results) {
                // we will only switch the language to the first result
                // if the preselected language in local storage does NOT match one of the options in the API
                const preselectedLanguage =
                    localStorage.getItem('i18nextLngHorizon')
                const languages = res.data?.results
                let selectedLanguage = res.data.results[0]
                if (preselectedLanguage) {
                    const preselectedOption = languages.find(
                        option => option.language === preselectedLanguage
                    )
                    if (preselectedOption) {
                        selectedLanguage = preselectedOption
                    }
                }
                this.setState(
                    {
                        languages,
                        selectedLanguage,
                    },
                    () => {
                        this.switchLanguage(this.state.selectedLanguage)
                    }
                )
            }
        } catch (ex) {
            console.error('Cannot load languages', ex)
        }
    }

    switchLanguage = languageOption => {
        this.setState(
            { selectedLanguage: languageOption, isOpen: false },
            () => {
                if (
                    localStorage.getItem('i18nextLngHorizon') !==
                    languageOption.language
                ) {
                    localStorage.setItem(
                        'i18nextLngHorizon',
                        languageOption.language
                    )
                    this.props.i18n.changeLanguage(languageOption.language)
                }
                if (document?.documentElement?.lang) {
                    document.documentElement.lang = languageOption.language
                }
            }
        )
    }

    componentDidMount() {
        this.loadLanguages()
    }

    renderLanguageOption = (option, isChecked) => {
        const label = `${option.language.toUpperCase()} - ${option.label}`
        return (
            <RadioCheckmark
                checked={isChecked}
                label={label}
                onChange={() => this.switchLanguage(option)}
                className={styles.languageOption}
                name={'languages'}
                value={option.label}
                key={option.language}
            />
        )
    }

    renderOptions = () => {
        // We want to render the selected language first
        const { languages, selectedLanguage } = this.state
        return (
            <>
                {this.renderLanguageOption(selectedLanguage, true)}
                {languages
                    .filter(
                        option => option.language !== selectedLanguage.language
                    )
                    .map(option => this.renderLanguageOption(option, false))}
            </>
        )
    }

    render() {
        const { languages, selectedLanguage, isOpen } = this.state
        if (languages.length <= 1) {
            return <></>
        }
        return (
            <Dropdown
                className={styles.dropdown}
                show={isOpen}
                onToggle={() => this.setState({ isOpen: !isOpen })}
            >
                <Dropdown.Toggle
                    as={SwitcherButton}
                    selectedLanguage={selectedLanguage}
                />
                <Dropdown.Menu className={styles.dropdownMenuContent}>
                    <span className={styles.changeLanguagePrompt}>
                        {this.props.t('Change language')}
                    </span>
                    {this.renderOptions()}
                </Dropdown.Menu>
            </Dropdown>
        )
    }
}

export default withTranslations(
    'common',
    'LanguageSwitchButton'
)(LanguageSwitchButton)
